import { Grid, TextInput, Select, Typography } from '@ts/storybook';
import Tooltip from '@components/Outputs/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import { WrapperEntitySelector } from '@components/Inputs';
import { generateSearchEntitySelector } from '@modules/entitySelector/entitySelector.utils';
import { CDeliveryChain, IForm } from '@ts/ui-models';
import { InputAdornment } from '@mui/material';

interface IDeliveryChainOptionsProp extends IForm {
  values: CDeliveryChain;
}

const DeliveryChainForwarderOptions = (props: IDeliveryChainOptionsProp) => {
  const { values, onCallbackChange } = props;
  const isViewMode = props.mode == 'view';
  const forwarderId = values.forwarderId;
  const interfaceTypes = [
    { value: 'LAN_MACAddr', label: 'LAN MAC Address' },
    { value: 'WiFi_MACAddr', label: 'Wi-Fi MAC Address' },
  ];
  const value = props.values.parameters?.infrastructureInterfaceTypeId;
  const isRequired = value === null || value === undefined; // Check if a value is selected

  //#region Renders
  const renderTiming = () => {
    return (
      <>
        <Grid container direction="row" spacing={2}>
          <Typography className="font-medium" text="Presence Detection" />
          <Tooltip title="Manage the lifecycle of an item's presence by defining its tracking criteria. These settings determine the window for continuous visibility and control how frequently the system broadcasts updates while an item remains in range.">
            <InfoIcon fontSize="small" />
          </Tooltip>
        </Grid>
        <TextInput
          id="presenceTimeoutMs"
          disabled={isViewMode}
          label="Presence Timeout"
          type="number"
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">ms</InputAdornment>,
            },
          }}
          value={props.values.parameters?.presenceTimeoutMs}
          placeholder="5000"
          onChangeValue={onCallbackChange('parameters.presenceTimeoutMs')}
          required={props.requiredFields?.includes('parameters.presenceTimeoutMs')}
          min={100}
          max={3600000} //1h
          infoText={
            'Maximum gap allowed between detections. If no reads arrive within this period, the item is considered out of coverage.'
          }
        />
        <TextInput
          id="updateIntervalMs"
          disabled={isViewMode}
          label="Update Interval"
          type="number"
          slotProps={{
            input: {
              endAdornment: <InputAdornment position="end">ms</InputAdornment>,
            },
          }}
          value={props.values.parameters?.updateIntervalMs}
          placeholder="10000"
          onChangeValue={onCallbackChange('parameters.updateIntervalMs')}
          required={props.requiredFields?.includes('parameters.updateIntervalMs')}
          min={1000}
          max={86400000} //24h
          infoText="A throttle applied during continuous presence. Emit detections no more than once per this interval."
        />
      </>
    );
  };

  const renderMahvis = () => {
    return (
      <>
        <Grid container direction="row" spacing={2}>
          <Typography className="font-medium" text="Default GNSS coordinates" />
          <Tooltip title="Enter the default GNSS coordinates that will be used in case a position is not achieved during a drop.">
            <InfoIcon fontSize="small" />
          </Tooltip>
        </Grid>
        <TextInput
          disabled={isViewMode}
          label="Latitude"
          type="number"
          value={props.values.parameters?.defaultGNSSCoordinates?.y}
          onChangeValue={onCallbackChange('parameters.defaultGNSSCoordinates.y')}
          required={props.requiredFields?.includes('parameters.defaultGNSSCoordinates.y')}
        />
        <TextInput
          disabled={isViewMode}
          label="Longitude"
          type="number"
          value={props.values.parameters?.defaultGNSSCoordinates?.x}
          onChangeValue={onCallbackChange('parameters.defaultGNSSCoordinates.x')}
          required={props.requiredFields?.includes('parameters.defaultGNSSCoordinates.x')}
        />
        <TextInput
          disabled={isViewMode}
          label="Z"
          type="number"
          value={props.values.parameters?.defaultGNSSCoordinates?.z}
          onChangeValue={onCallbackChange('parameters.defaultGNSSCoordinates.z')}
          required={props.requiredFields?.includes('parameters.defaultGNSSCoordinates.z')}
        />
        <Typography className="font-medium" text="Select the type of events triggered on PICK actions from LiftTraks" />
        <WrapperEntitySelector
          searchComponent={generateSearchEntitySelector('eventType')}
          value={props.values.parameters?.pickEventTypeId}
          onChange={onCallbackChange('parameters.pickEventTypeId')}
          isReadOnly={isViewMode}
          multiple={false}
        />
        <Typography className="font-medium" text="Select the type of events triggered on DROP actions from LiftTraks" />
        <WrapperEntitySelector
          searchComponent={generateSearchEntitySelector('eventType')}
          value={props.values.parameters?.dropEventTypeId}
          onChange={onCallbackChange('parameters.dropEventTypeId')}
          isReadOnly={isViewMode}
          multiple={false}
        />
      </>
    );
  };

  const renderOptions = () => {
    switch (forwarderId) {
      case 'mokoMKGW1':
      case 'chainwayRFIDReader':
      case 'impinjSpeedwayConnect':
        return (
          <Select
            label={'Select interface type'}
            value={value}
            onChange={v => onCallbackChange('parameters.infrastructureInterfaceTypeId')(v)}
            disabled={isViewMode}
            options={interfaceTypes}
            required={isRequired}
          />
        );
      case 'mahvis':
        return renderMahvis();

      default:
        return;
    }
  };
  const renderOptionsTiming = () => {
    switch (forwarderId) {
      case 'zebraIoTConnector':
      case 'impinjSpeedwayConnect':
      case 'impinjIoTDeviceInterface':
      case 'chainwayRFIDReader':
        return renderTiming();
      default:
        return;
    }
  };

  //#endregion

  /*   useEffect(() => {
    //Remove forwarder options
    const paremeters = { ...values.parameters };
    delete paremeters.infrastructureInterfaceTypeId;
    delete paremeters.defaultGNSSCoordinates;
    delete paremeters.pickEventTypeId;
    delete paremeters.dropEventTypeId;

    onCallbackChange('parameters')(paremeters);
  }, [forwarderId]); */

  if (!forwarderId) return;
  return (
    <>
      {renderOptions()}
      {renderOptionsTiming()}
    </>
  );
};

export default DeliveryChainForwarderOptions;
